'use client'

import { motion } from 'framer-motion'
import { Rocket, CheckCircle2 } from 'lucide-react'

interface RolloutStep {
  title: string
  description: string
}

interface AutomotiveRolloutSectionProps {
  heading: string
  intro: string
  note?: string
  steps: RolloutStep[]
}

export function AutomotiveRolloutSection({ heading, intro, note, steps }: AutomotiveRolloutSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-background">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
          >
            <Rocket className="h-4 w-4 text-primary" />
            <span className="eyebrow">
              Implementation Roadmap
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
          >
            {heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="reveal reveal-delay-2 text-lg text-muted-foreground mb-4"
          >
            {intro}
          </motion.p>
          {note && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="reveal reveal-delay-3 text-sm text-muted-foreground/80"
            >
              {note}
            </motion.p>
          )}
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.15,
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="reveal group relative rounded-3xl overflow-hidden bg-white dark:bg-zinc-900 p-8 transition-all duration-300 lift-hover"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                transitionDelay: `${(index % 4) * 80}ms`,
              }}
            >
              {/* Gradient Glow */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-2/3 opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: `radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)`,
                  filter: 'blur(40px)',
                }}
              />

              {/* Bottom Border Glow */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-[2px]"
                style={{
                  background: `linear-gradient(90deg, rgba(47, 95, 232, 0.1) 0%, rgba(47, 95, 232, 0.5) 50%, rgba(47, 95, 232, 0.1) 100%)`,
                  boxShadow: `0 0 15px 3px rgba(47, 95, 232, 0.2)`,
                }}
              />

              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#2F5FE8]/10 to-[#2F5FE8]/20 dark:from-[#2F5FE8]/20 dark:to-[#2F5FE8]/30 flex items-center justify-center border border-[#2F5FE8]/20 dark:border-[#2F5FE8]/20 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-lg font-bold text-[#2F5FE8] dark:text-[#7EA0F5]">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-3">
                      <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                        Step {index + 1}
                      </span>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-foreground mb-4">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm text-primary">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-medium">Ready to implement</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
